/**
 * Step 3: EXPRESS-only store OPEN vs CLOSED from brand_schedule hours.
 * No UI, no scheduling, no checkout changes. Debug logs only.
 * Orchestrates Steps 1–3 in one pipeline (single fetch, ordered logs).
 */
(function (global) {
  function normalizeOffDays(value) {
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return normalizeOffDays(parsed);
      } catch {
        /* fall through */
      }

      return trimmed
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }

  function parseHour(time) {
    return parseInt(String(time || '0:00').split(':')[0], 10);
  }

  /**
   * @param {object | null | undefined} schedule raw row from brand_schedule JSON
   * @param {Date} [now]
   * @returns {boolean} true if store is CLOSED
   */
  function isStoreClosed(schedule, now) {
    const n = now instanceof Date ? now : new Date();
    const currentHour = n.getHours();
    const currentDay = n.toLocaleDateString('en-US', { weekday: 'long' });

    if (!schedule) return false;

    const openHour = parseHour(schedule.open_time);
    const closeHour = parseHour(schedule.close_time);

    const offList = normalizeOffDays(schedule.off_days);
    const onOffDay = offList.some(
      (d) => String(d).trim().toLowerCase() === currentDay.toLowerCase()
    );

    const outsideHours =
      Number.isFinite(openHour) &&
      Number.isFinite(closeHour) &&
      (currentHour < openHour || currentHour >= closeHour);

    const isClosed =
      schedule.is_closed_today === true ||
      schedule.is_closed_today === 'true' ||
      schedule.is_closed_today === 1 ||
      onOffDay ||
      outsideHours;

    return isClosed;
  }

  function findScheduleByVendor(schedules, vendor) {
    const n = global.SkippDeliveryClassification?.normalize;
    const normalizeFn = typeof n === 'function' ? n : (str) => (str || '').trim().toLowerCase();
    const key = normalizeFn(vendor);
    if (!key) return null;
    return schedules.find((s) => normalizeFn(s?.brand_name) === key) || null;
  }

  /**
   * @param {object[]} cartWithETA from Step 2
   * @param {object[]} schedules raw from [data-brand-schedules-json]
   * @param {Date} [now]
   */
  function enrichCartWithStoreStatus(cartWithETA, schedules, now) {
    const effectiveNow = now instanceof Date ? now : new Date();
    const EXPRESS = global.SkippDeliveryClassification?.EXPRESS || 'EXPRESS';

    const cartWithStoreStatus = (cartWithETA || []).map((item) => {
      const treatAsExpressForStoreHours = item.deliveryType === EXPRESS;

      if (!treatAsExpressForStoreHours) {
        return {
          ...item,
          storeStatus: null,
        };
      }

      const schedule = findScheduleByVendor(schedules, item.vendor);
      const closed = isStoreClosed(schedule, effectiveNow);

      return {
        ...item,
        storeStatus: closed ? 'CLOSED' : 'OPEN',
      };
    });

    const hasClosedExpress = cartWithStoreStatus.some((row) => row.storeStatus === 'CLOSED');

    return { cartWithStoreStatus, hasClosedExpress };
  }

  async function runDeliveryDebugPipelineFromFetch() {
    const D = global.SkippDeliveryClassification;
    const E = global.SkippDeliveryEtaCalculation;
    if (!D || !E) return null;

    const userCity = D.resolveUserCityString();
    const schedules = E.parseBrandSchedules();
    const userCoords = E.getUserCoords();

    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const cart = await res.json();

      const step1 = D.analyzeCart(cart, userCity);
      const step2 = E.enrichCartAnalysisWithEta(step1.cartAnalysis, cart, schedules, userCoords);
      const step3 = enrichCartWithStoreStatus(step2.cartWithETA, schedules, undefined);

      const payload = {
        cart,
        schedules,
        ...step1,
        ...step2,
        ...step3,
      };
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('skipp:delivery-pipeline-complete', { detail: payload }));
      }
      return payload;
    } catch {
      return null;
    }
  }

  function runDeliveryDebugPipelineFromCartPayload(cart) {
    const D = global.SkippDeliveryClassification;
    const E = global.SkippDeliveryEtaCalculation;
    if (!D || !E || !cart || !Array.isArray(cart.items)) return null;

    const userCity = D.resolveUserCityString();
    const schedules = E.parseBrandSchedules();
    const userCoords = E.getUserCoords();

    const step1 = D.analyzeCart(cart, userCity);
    const step2 = E.enrichCartAnalysisWithEta(step1.cartAnalysis, cart, schedules, userCoords);
    const step3 = enrichCartWithStoreStatus(step2.cartWithETA, schedules, undefined);

    const payload = {
      cart,
      schedules,
      ...step1,
      ...step2,
      ...step3,
    };
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('skipp:delivery-pipeline-complete', { detail: payload }));
    }
    return payload;
  }

  global.SkippDeliveryStoreStatus = {
    normalizeOffDays,
    parseHour,
    isStoreClosed,
    findScheduleByVendor,
    enrichCartWithStoreStatus,
    runDeliveryDebugPipelineFromFetch,
    runDeliveryDebugPipelineFromCartPayload,
  };

  document.addEventListener('DOMContentLoaded', () => {
    runDeliveryDebugPipelineFromFetch();
  });

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail?.cartData;
    if (cart) runDeliveryDebugPipelineFromCartPayload(cart);
  });
})(typeof window !== 'undefined' ? window : globalThis);
